/**
 * Fetches the rows buildOwnerTenancyActivity needs, scoped to one owner's own
 * apartments — never portfolio-wide, matching the rule the owner-lifecycle
 * mails already follow.
 *
 * Deliberately a second hook rather than more columns on useOwnerEmailHistory,
 * even though the two overlap: they answer different questions off the same
 * records ("did it happen" vs "were they told"), they are cached and
 * invalidated independently, and the email-history hook has one query it must
 * NOT degrade (the send-log) while every query here is best-effort — folding
 * them together would mean one shared failure mode for both.
 *
 * Every query below degrades to [] on its own. A tenancy timeline missing its
 * protocols is a shorter timeline; it is not a broken tab, and the Activity
 * tab already warns separately when the logged (audit) half fails to load.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  buildOwnerTenancyActivity,
  type OwnerTenancyRentReceipt,
} from "@/lib/owner-tenancy-activity";
import type { OwnerActivityEvent } from "@/lib/owner-activity";

/**
 * How far back rent receipts are read. The builder also caps how many render;
 * this keeps the query itself from scanning an entire tenancy history to
 * produce rows that would be trimmed anyway. Matches the 12-month window
 * useOwnerEmailHistory applies to management invoices.
 */
const RENT_WINDOW_MONTHS = 12;

function rentWindowStart(now = new Date()): string {
  const d = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - RENT_WINDOW_MONTHS, 1)
  );
  return d.toISOString();
}

const BOOKING_SELECT =
  "id, name, contract_signed_at, key_picked_up_at, move_out_notice_at, " +
  "move_out_date, key_returned_at, rooms!inner(apartment_id, property_id, apartments(label))";

export function useOwnerTenancyActivity(
  lessorId: string | null | undefined,
  apartmentIds: Array<number | null>,
  /**
   * Properties where this owner holds rooms with no apartment record
   * (`useOwnerDetail`'s `unassignedUnits`). Those rooms have no apartment id
   * to filter bookings by, so their property id is the only handle — see
   * `owner-tenancy-activity.ts`'s header for why apartment-only filtering
   * silently dropped this whole subset before.
   */
  unassignedPropertyIds: Array<number | null> = []
) {
  const ids = Array.from(
    new Set(apartmentIds.filter((id): id is number => id != null))
  );
  const propIds = Array.from(
    new Set(unassignedPropertyIds.filter((id): id is number => id != null))
  );

  return useQuery<OwnerActivityEvent[]>({
    queryKey: [
      "owner-tenancy-activity",
      lessorId,
      ids.slice().sort(),
      propIds.slice().sort(),
    ],
    enabled: !!lessorId && (ids.length > 0 || propIds.length > 0),
    queryFn: async () => {
      const [assignedRes, unassignedRes] = await Promise.all([
        ids.length > 0
          ? supabase
              .from("bookings")
              .select(BOOKING_SELECT)
              .in("rooms.apartment_id", ids)
          : Promise.resolve({ data: [], error: null }),
        propIds.length > 0
          ? supabase
              .from("bookings")
              .select(BOOKING_SELECT)
              .is("rooms.apartment_id", null)
              .in("rooms.property_id", propIds)
          : Promise.resolve({ data: [], error: null }),
      ]);
      // No bookings (or an unreadable list) means no tenancy events — the
      // three enrichment queries below are all keyed on booking ids.
      const bookingRows: any[] = [
        ...(assignedRes.error ? [] : (assignedRes.data ?? [])),
        ...(unassignedRes.error ? [] : (unassignedRes.data ?? [])),
      ];
      const bookingIds = bookingRows.map((b) => b.id);
      if (bookingIds.length === 0) {
        return buildOwnerTenancyActivity({ bookings: [], rentReceipts: [] });
      }

      const [depositRes, rentRes, protocolsRes] = await Promise.all([
        supabase
          .from("payments")
          .select("booking_id, paid_at")
          .eq("type", "deposit")
          .eq("status", "paid")
          .in("booking_id", bookingIds),
        supabase
          .from("payments")
          .select("booking_id, paid_at, amount_cents, due_date")
          .eq("type", "rent")
          .eq("status", "paid")
          .in("booking_id", bookingIds)
          .gte("paid_at", rentWindowStart())
          .order("paid_at", { ascending: false }),
        supabase
          .from("handover_protocols")
          .select("booking_id, kind, submitted_at")
          .eq("status", "submitted")
          .in("booking_id", bookingIds),
      ]);

      const depositPayments: any[] = depositRes.error
        ? []
        : (depositRes.data ?? []);
      const rentPayments: any[] = rentRes.error ? [] : (rentRes.data ?? []);
      const protocols: any[] = protocolsRes.error
        ? []
        : (protocolsRes.data ?? []);

      // Latest paid deposit per booking — an instalment-paid deposit is two
      // rows and one "deposit received" event.
      const depositPaidAtByBooking = new Map<number, string>();
      for (const p of depositPayments) {
        if (!p.paid_at) continue;
        const existing = depositPaidAtByBooking.get(p.booking_id);
        if (!existing || p.paid_at > existing) {
          depositPaidAtByBooking.set(p.booking_id, p.paid_at);
        }
      }
      const protocolSubmittedAtByBooking = new Map<string, string>();
      for (const p of protocols) {
        if (!p.submitted_at) continue;
        const key = `${p.booking_id}:${p.kind}`;
        const existing = protocolSubmittedAtByBooking.get(key);
        if (!existing || p.submitted_at > existing) {
          protocolSubmittedAtByBooking.set(key, p.submitted_at);
        }
      }

      return buildOwnerTenancyActivity({
        bookings: bookingRows.map((b: any) => ({
          id: b.id,
          tenant_name: b.name ?? null,
          apartment_label: b.rooms?.apartments?.label ?? null,
          contract_signed_at: b.contract_signed_at ?? null,
          deposit_paid_at: depositPaidAtByBooking.get(b.id) ?? null,
          key_picked_up_at: b.key_picked_up_at ?? null,
          move_in_protocol_submitted_at:
            protocolSubmittedAtByBooking.get(`${b.id}:move_in`) ?? null,
          move_out_notice_at: b.move_out_notice_at ?? null,
          move_out_date: b.move_out_date ?? null,
          move_out_protocol_submitted_at:
            protocolSubmittedAtByBooking.get(`${b.id}:move_out`) ?? null,
          key_returned_at: b.key_returned_at ?? null,
        })),
        rentReceipts: rentPayments
          .filter((p: any) => !!p.paid_at)
          .map(
            (p: any): OwnerTenancyRentReceipt => ({
              booking_id: p.booking_id,
              paid_at: p.paid_at,
              amount_cents: p.amount_cents ?? null,
              due_date: p.due_date ?? null,
            })
          ),
      });
    },
  });
}
